import {bbox} from '@turf/turf';

import {isRectanglePolygon} from 'app/utils/geoJsonUtils';
import * as historyUtils from 'app/utils/historyUtils';
import * as routeUtils from 'app/utils/routeUtils';

import {CartItemV1} from './types';

/** The cart page for an organization, the same route `AppRouter` mounts it at. */
export function cartPageUrl(
  organizationId: string,
  {includeRoot = false}: {includeRoot?: boolean} = {}
): string {
  const root = includeRoot ? historyUtils.ROOT_URL : '';
  return `${root}/${routeUtils.makeUuidPrefix(organizationId)}/cart`;
}

/** The fields a link to a cart item's scene is built from, so an unsaved draft can be linked too. */
export type CartItemLinkFields = Pick<
  CartItemV1,
  'projectId' | 'featureId' | 'sourceId' | 'sensingTime' | 'geometry'
>;

/**
 * The stored sub-area in the same form the modal builds from its own draw state, so a round trip
 * through the deep link — and a comparison against what the modal currently has — line up exactly.
 */
export function cartItemOrderBboxString(item: Pick<CartItemV1, 'geometry'>): string | undefined {
  return item.geometry ? JSON.stringify(bbox(item.geometry)) : undefined;
}

/** Link to the item's property on the map, without reopening a scene. */
export function cartItemPropertyLink(item: CartItemV1, organizationId: string): string {
  return routeUtils.makeProjectDashboardUrl(
    organizationId,
    item.projectId,
    'map',
    [String(item.featureId)],
    undefined,
    {includeRoot: false}
  );
}

/**
 * Link back to the scene an item was added from, opening MonitorProjectView with the order modal on
 * that scene. A single-rectangle sub-area rides along as the bbox param `UrlSceneHandler` rehydrates
 * into the subset flow, so the modal reopens configured the way it was when the item was added. A
 * multi-rectangle sub-area can't be expressed as one bbox, so those items link to the scene alone.
 *
 * The feature segment is the numeric feature id: cart items don't store a lens uuid, and the route
 * resolver (`routeUtils.featureMatchesParam`) accepts either form.
 */
export function cartItemDeepLink(
  item: CartItemLinkFields,
  organizationId: string,
  {includeRoot = false}: {includeRoot?: boolean} = {}
): string {
  const orderBboxString = isRectanglePolygon(item.geometry)
    ? cartItemOrderBboxString(item)
    : undefined;
  return routeUtils.makeHighResSceneUrl({
    organizationId,
    projectId: item.projectId,
    featureLensId: String(item.featureId),
    sourceId: item.sourceId,
    date: item.sensingTime,
    ...(orderBboxString ? {orderBboxString} : {}),
    includeRoot,
  });
}
